import * as React from 'react';
import {
  BrandMark,
  Button,
  MobileIntro,
  SetupChecklist,
  SetupStep,
} from '@relay-towing/ui';
import { useNavigate } from 'react-router-dom';
import { useCompanyInfo } from '../../api/hooks/useCompanyInfo';
import { useMember } from '../../hooks/useMember';
import { dismiss, isDismissed } from '../../lib/dismissals';
import { OPERATION_ITEMS } from '../../lib/driverAppTabs';
import { links } from '../../lib/links';
import { openExternal } from '../../lib/nativeBridge';
import { finishOnboarding } from '../../lib/onboardingGate';
import { BusinessProfileSheet } from './BusinessProfileSheet';

/**
 * Where Operations' Team members opens: the dashboard's accounts tab, where
 * people are invited and given their office role. The phone has no invite
 * flow of its own, so setup opens the same page rather than a second one.
 */
const TEAM_PAGE = (() => {
  const team = OPERATION_ITEMS.find((item) => item.id === 'team');
  return team && 'dashboard' in team.opens
    ? team.opens.dashboard
    : '/settings?tab=accounts';
})();

/**
 * The setup checklist, the Figma's (123:7153): a new owner's first steps,
 * each a row that ticks off. It replaces the old shell's four-step
 * onboarding at /onboarding in the new shell.
 *
 * - Business Profile opens its sheet, with "Edit company settings", and
 *   is done once the company has a name, an address and a phone.
 * - Invite your team, with "Add users", and Assign admins, with "Edit
 *   users" (giving someone an office role), open the dashboard's team page
 *   in the browser, as Operations' Team members does. The server cannot say
 *   yet whether anyone was invited, so these tick once opened from here,
 *   remembered on this device.
 *
 * A row the member's role cannot do is not shown. Finish Set Up and Skip
 * for now both go Home, and setup does not open again on this device.
 * The old shell's Team Member Apps step (install links) is not here: the
 * frame leaves it out.
 */
export function SetupScreen() {
  const navigate = useNavigate();
  const member = useMember();
  const company = useCompanyInfo();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [opened, setOpened] = React.useState(() => ({
    team: isDismissed('setup:team'),
    admins: isDismissed('setup:admins'),
  }));

  const profile = company.data;
  const profileDone = Boolean(
    profile?.name?.trim() &&
      profile.streetAddress?.trim() &&
      profile.companyPhone?.trim()
  );

  const openTeam = (step: 'team' | 'admins') => {
    dismiss(`setup:${step}`);
    setOpened((was) => ({ ...was, [step]: true }));
    openExternal(links.dashboardPage(TEAM_PAGE));
  };

  const done = () => {
    finishOnboarding();
    navigate('/', { replace: true });
  };

  return (
    <>
      <MobileIntro
        align="top"
        brand={<BrandMark name="relay" size="md" />}
        title="Let’s get you set up and ready to operate"
        description="Just a few things remain to start dispatching."
        actions={
          <>
            <Button size="xl" pill variant="soft" onClick={done}>
              Finish Set Up
            </Button>
            <Button variant="link" onClick={done}>
              Skip for now
            </Button>
          </>
        }
      >
        <SetupChecklist>
          {member.can('company.edit') ? (
            <SetupStep
              title="Business Profile"
              description="Name and location"
              done={profileDone}
              onClick={() => setProfileOpen(true)}
            />
          ) : null}
          {member.can('users.add') ? (
            <SetupStep
              title="Invite your team"
              description="Add your team to Relay"
              done={opened.team}
              external
              onClick={() => openTeam('team')}
            />
          ) : null}
          {member.can('users.edit') ? (
            <SetupStep
              title="Assign admins"
              description="Create admin roles from your team"
              done={opened.admins}
              external
              onClick={() => openTeam('admins')}
            />
          ) : null}
        </SetupChecklist>
      </MobileIntro>
      <BusinessProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}
